import { z } from 'zod';
import { hasMaxDecimals } from '../utils/number';

/**
 * Schémas Zod pour depenses — validation côté form (RHF) et côté queries.
 * Aligne sur les CHECK constraints de la migration SQL :
 *   supabase/migrations/20260515120000_sprint3_depenses_tables.sql
 *   supabase/migrations/20260515125000_sprint3_depenses_lignes.sql
 */

// ─── Enums (mirror DB) ────────────────────────────────────
export const DepenseStatusSchema = z.enum([
  'demandée',
  'en_cours_transfert',
  'payée',
  'comptabilisée',
  'annulée',
]);
export const DepenseApprovalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const DepenseModePaiementSchema = z.enum(['wallet', 'cheque', 'transfer']);
export const DepenseDeviseSchema = z.enum(['MRU', 'EUR', 'USD']);
export const DepenseOcrConfianceSchema = z.enum(['haute', 'moyenne', 'basse']);
export const DepenseWalletTypeSchema = z.enum([
  'bankily',
  'masrivi',
  'sedad',
  'bimbank',
  'click',
  'bcipay',
]);
export const DepenseWalletVerifStatusSchema = z.enum(['ok', 'warning', 'error']);
export const DepenseAnomalyTypeSchema = z.enum([
  'doublon_facture',
  'montant_suspect',
  'beneficiaire_montants_croissants',
  'fournisseur_nouveau',
  'photo_floue',
  'wallet_mismatch',
  'round_number',
  'heure_atypique',
  'tva_incoherence',
  'nif_etranger_sans_swift',
  'prix_ligne_suspect',
]);
export const DepenseAnomalySeveritySchema = z.enum(['info', 'warning', 'error']);

// ─── Primitives métier ────────────────────────────────────

/** Montant MRU positif, 2 décimales max (numeric(14,2)). */
export const MontantMRUSchema = z
  .number()
  .positive('Le montant doit être strictement positif')
  .finite('Le montant doit être un nombre fini')
  .refine((v) => hasMaxDecimals(v, 2), {
    message: 'Le montant ne peut pas avoir plus de 2 décimales',
  });

/** Numéro de facture : non-vide, trim. */
export const NumFactureSchema = z.string().trim().min(1, 'Numéro de facture requis');

/** NIF mauritanien : chiffres + optionnellement préfixe lettres (ex. "12345678"). */
export const NIFSchema = z.string().trim().min(4, 'NIF trop court').max(32, 'NIF trop long');

/** Bénéficiaire : 1-200 caractères. */
export const BeneficiaireSchema = z
  .string()
  .trim()
  .min(1, 'Bénéficiaire obligatoire')
  .max(200, 'Bénéficiaire trop long');

/** Taux de change positif. */
export const TauxChangeSchema = z.number().positive('Taux de change strictement positif').finite();

// ─── Création d'une dépense ──────────────────────────────
const DepenseCreateBase = z.object({
  beneficiaire: BeneficiaireSchema,
  fournisseurId: z.string().uuid().nullable().optional(),
  fournisseurNom: z.string().trim().min(1).optional(),

  categorie: z.string().trim().min(1).nullable().optional(),
  description: z.string().trim().nullable().optional(),

  montant: MontantMRUSchema,
  modePaiementSouhaite: DepenseModePaiementSchema,

  // Compte de trésorerie débité (FK comptes_tresorerie)
  devise: DepenseDeviseSchema.nullable().optional(),
  paidCompteId: z.string().uuid().nullable().optional(),

  // Wallet
  walletType: DepenseWalletTypeSchema.nullable().optional(),
  walletNumero: z.string().trim().min(1).nullable().optional(),

  // Facture
  numFacture: NumFactureSchema.nullable().optional(),
  dateFacture: z.date().nullable().optional(),
  nifFournisseur: NIFSchema.nullable().optional(),

  // OCR devise étrangère (Lot 2)
  ocrDeviseDetectee: DepenseDeviseSchema.nullable().optional(),
  ocrMontantOrigine: MontantMRUSchema.nullable().optional(),
  ocrTauxChangeMru: TauxChangeSchema.nullable().optional(),

  justificatifsDemandeUrls: z.array(z.string().url()).default([]),

  requestedByEmail: z.string().email('Email demandeur invalide'),
  createdByEmail: z.string().email('Email créateur invalide'),
});

export const DepenseCreateInputSchema = DepenseCreateBase.superRefine((data, ctx) => {
  // CHECK SQL : devise SWIFT-only (transfer => EUR/USD, sinon NULL)
  if (data.modePaiementSouhaite === 'transfer') {
    if (data.devise !== 'EUR' && data.devise !== 'USD') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['devise'],
        message: 'Mode transfer requiert devise EUR ou USD',
      });
    }
    if (!data.paidCompteId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paidCompteId'],
        message: 'Compte bancaire requis pour mode transfer',
      });
    }
  } else if (data.devise != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['devise'],
      message: 'Devise réservée au mode transfer',
    });
  }

  // Mode wallet => type + numero requis
  if (data.modePaiementSouhaite === 'wallet') {
    if (!data.walletType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['walletType'],
        message: 'Type de wallet requis (Bankily, Masrivi…)',
      });
    }
    if (!data.walletNumero) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['walletNumero'],
        message: 'Numéro de wallet requis',
      });
    }
  }

  // CHECK SQL ocr_devise_coherence : si ocrDeviseDetectee → montant + taux requis
  if (data.ocrDeviseDetectee != null) {
    if (data.ocrMontantOrigine == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ocrMontantOrigine'],
        message: 'Montant en devise origine requis si devise OCR renseignée',
      });
    }
    if (data.ocrTauxChangeMru == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ocrTauxChangeMru'],
        message: 'Taux de change MRU requis si devise OCR renseignée',
      });
    }
  }
});

// ─── Sprint 3.5 : Feedback Sidi (texte libre) ────────────
export const LigneFeedbackInputSchema = z.object({
  anomalyId: z.string().uuid().nullable().optional(),
  ligneId: z.string().uuid(),
  raisonLibre: z
    .string()
    .trim()
    .min(3, 'Raison trop courte (min 3 caracteres)')
    .max(2000, 'Raison trop longue (max 2000 caracteres)'),
  accepted: z.boolean().default(true),
  createdByEmail: z.string().email(),
});

export type LigneFeedbackInput = z.infer<typeof LigneFeedbackInputSchema>;

// ─── Approbation (Q8 : daf seulement, transfer exempt) ────
export const DepenseApproveInputSchema = z.object({
  depenseId: z.string().uuid(),
  approvalByEmail: z.string().email(),
});

export const DepenseRejectInputSchema = z.object({
  depenseId: z.string().uuid(),
  approvalByEmail: z.string().email(),
  rejectionReason: z
    .string()
    .trim()
    .min(1, 'Raison du rejet obligatoire')
    .max(1000, 'Raison trop longue (max 1000)'),
});

// ─── Paiement ─────────────────────────────────────────────
const DepensePayBase = z.object({
  depenseId: z.string().uuid(),
  paidByEmail: z.string().email(),
  modePaiementSouhaite: DepenseModePaiementSchema,
  paidAt: z.date().optional(),

  // Wallet
  walletType: DepenseWalletTypeSchema.nullable().optional(),
  walletNumero: z.string().trim().min(1).nullable().optional(),

  // Cheque
  paidNumCheque: z.string().trim().min(1).nullable().optional(),

  // Compte de trésorerie débité
  paidCompteId: z.string().uuid().nullable().optional(),
  devise: DepenseDeviseSchema.nullable().optional(),

  justificatifsPaiementUrls: z.array(z.string().url()).default([]),
});

export const DepensePayInputSchema = DepensePayBase.superRefine((data, ctx) => {
  if (data.modePaiementSouhaite === 'cheque' && !data.paidNumCheque) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['paidNumCheque'],
      message: 'Numéro de chèque requis',
    });
  }
  if (data.modePaiementSouhaite === 'wallet') {
    if (!data.walletType || !data.walletNumero) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['walletNumero'],
        message: 'Type et numéro de wallet requis',
      });
    }
  }
  if (data.modePaiementSouhaite === 'transfer') {
    if (!data.paidCompteId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paidCompteId'],
        message: 'Compte bancaire requis',
      });
    }
    if (data.devise !== 'EUR' && data.devise !== 'USD') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['devise'],
        message: 'Mode transfer requiert devise EUR ou USD',
      });
    }
  }
});

// ─── Comptabilisation Sage ────────────────────────────────
export const DepenseComptabiliseInputSchema = z.object({
  depenseId: z.string().uuid(),
  comptabiliseByEmail: z.string().email(),
  numeroPieceSage: z.string().trim().min(1, 'Numéro de pièce Sage obligatoire'),
});

// ─── Annulation soft (V2 ajout) ───────────────────────────
export const DepenseAnnuleInputSchema = z.object({
  depenseId: z.string().uuid(),
  par: z.string().trim().min(1),
  raison: z.string().trim().min(1, "Raison d'annulation obligatoire").max(500),
});

// ─── Fournisseur (Q5) ─────────────────────────────────────
export const FournisseurCreateInputSchema = z.object({
  nom: z.string().trim().min(1, 'Nom fournisseur requis').max(200),
  nif: NIFSchema.nullable().optional(),
  aliases: z.array(z.string().trim().min(1)).default([]),
  nifEtranger: z.boolean().default(false),

  defaultCategorie: z.string().trim().nullable().optional(),
  defaultModePaiement: DepenseModePaiementSchema.nullable().optional(),
  defaultWalletType: DepenseWalletTypeSchema.nullable().optional(),
  defaultWalletNumero: z.string().trim().nullable().optional(),
  defaultPaidCompte: z.string().trim().nullable().optional(),
  defaultDevise: DepenseDeviseSchema.nullable().optional(),
});

// ─── Ligne de facture (OCR-driven Sprint 3) ───────────────
export const DepenseLigneInputSchema = z.object({
  ligneNo: z.number().int().min(1),
  libelle: z.string().trim().min(1, 'Libellé ligne requis').max(500),
  quantite: z.number().positive().nullable().optional(),
  unite: z.string().trim().nullable().optional(),
  prixUnitaire: z.number().nonnegative().nullable().optional(),
  montantLigne: z.number().nonnegative().nullable().optional(),
  factureIndex: z.number().int().min(0).default(0),
  ocrConfiance: DepenseOcrConfianceSchema.nullable().optional(),
});

// ─── Inférences de type ───────────────────────────────────
export type DepenseCreateInput = z.infer<typeof DepenseCreateInputSchema>;
export type DepensePayInput = z.infer<typeof DepensePayInputSchema>;
export type DepenseApproveInput = z.infer<typeof DepenseApproveInputSchema>;
export type DepenseRejectInput = z.infer<typeof DepenseRejectInputSchema>;
export type DepenseComptabiliseInput = z.infer<typeof DepenseComptabiliseInputSchema>;
export type DepenseAnnuleInput = z.infer<typeof DepenseAnnuleInputSchema>;
export type FournisseurCreateInput = z.infer<typeof FournisseurCreateInputSchema>;
export type DepenseLigneInput = z.infer<typeof DepenseLigneInputSchema>;
